import React from 'react';
import { X } from 'lucide-react';
import { C } from '../../theme.js';
import { Button, Eyebrow, FieldLabel, IconButton, TextInput } from '../../components/Primitives.jsx';
import { SOURCE_COLUMNS } from '../../data.js';
import { ColumnsContext, Segmented } from '../StepInspector.jsx';
import { ConditionRows } from '../shared/ConditionRows.jsx';
import { ResultValueField } from '../shared/ResultValueField.jsx';
import { OutputColumnField } from '../shared/OutputColumnField.jsx';
import { OutputNameField } from '../shared/OutputNameField.jsx';
import { SourceSelect } from '../shared/SourceSelect.jsx';
import { sourceColumns } from '../shared/sourceColumns.js';
import { outputNameIssue, pipelineOrder } from '../../data.js';
import { ExpressionEditorModal } from './ExpressionEditorModal.jsx';
import { isCodeCase, needsReview, oneLine } from './codeMode/caseWhenSlot.js';
import {
  blockToggleWrite, builderBlockPatch, codeBlockPatch, firstLines, hasSavedBlockCode,
  isCodeBlock, needsBlockReview, outputNamesOf, validateOutputNames,
} from './codeMode/blockCode.js';
import { resolveEditorRows } from './codeMode/rowSource.js';
import { blockConvertSource, showFirstOpenHint } from './codeMode/convertFromBuilder.js';

// Same Source-anchored contract as Compute/Filter: when Source isn't self,
// cases evaluate over that Source's rows, self is left unchanged for later
// steps, and the result is only reachable through Output Name.
export function CaseWhenInspector({ step, onChange, columns, pipeline, runMap, secondaryDatasets, sheets, onBrowseJoinFile, uploadedRows }) {
  const ctxCols = React.useContext(ColumnsContext);
  const c = step.config || {};
  const sourceIsSelf = !c.source || c.source === '__self__';
  const liveCols = columns || ctxCols || SOURCE_COLUMNS;
  const cols = sourceIsSelf ? liveCols : (sourceColumns(c.source, { sheets, secondaryDatasets, pipeline, runMap }) || liveCols);
  const outputNameIssueRes = outputNameIssue(c.output_name, { sheets, secondaryDatasets, steps: pipeline?.steps, stepId: step.id });
  const outputNameMissing = !sourceIsSelf && !(c.output_name || '').trim();
  const cases = c.cases || [];
  const u = (patch) => onChange({ ...step, config: { ...c, ...patch } });
  const updateCase = (i, patch) => u({ cases: cases.map((cs, j) => j === i ? { ...cs, ...patch } : cs) });
  const addCase = () => u({ cases: [...cases, { when: [{ column: (cols[0]?.name || ''), op: 'eq', value: '' }], then: '' }] });
  // Block-level code mode UI state. Choosing "Code" with nothing saved stays
  // local (`codeDraft`) and opens the editor: the server treats a blank code
  // source as malformed, so logic_mode:"code" and code are written together,
  // only by Apply. Closing that first editor without Apply writes nothing and
  // the toggle returns to Builder.
  const [codeDraft, setCodeDraft] = React.useState(false);
  const [editing, setEditing] = React.useState(false);
  React.useEffect(() => { setCodeDraft(false); setEditing(false); }, [step.id]);
  const codeView = isCodeBlock(c) || codeDraft;
  const chooseBlockMode = (next) => {
    if (next === 'code') {
      const write = blockToggleWrite(c);
      if (write) u(write);
      else { setCodeDraft(true); setEditing(true); }
    } else {
      setCodeDraft(false);
      if (isCodeBlock(c)) u(builderBlockPatch);
    }
  };
  const removeCase = (i) => u({ cases: cases.filter((_, j) => j !== i) });
  const editorRows = React.useMemo(() => resolveEditorRows({
    source: c.source, orderedSteps: pipelineOrder(pipeline?.steps || []), stepId: step.id,
    runMap, sheets, secondaryDatasets, uploadedRows, steps: pipeline?.steps,
  }), [c.source, pipeline?.steps, step.id, runMap, sheets, secondaryDatasets, uploadedRows]);
  // ── Extra output columns: keyed by target NAME (cases[].sets[name],
  // extra_outputs[].target), never by position, so reordering or removing
  // cases can't misalign values.
  const extras = c.extra_outputs || [];
  const uniqueName = () => {
    const taken = new Set([c.target || 'result', ...extras.map(e => e.target)]);
    let n = 1; let name = 'new_column';
    while (taken.has(name)) { n += 1; name = `new_column_${n}`; }
    return name;
  };
  const addExtra = () => u({ extra_outputs: [...extras, { target: uniqueName() }] });
  const patchExtra = (idx, patch) => u({ extra_outputs: extras.map((e, k) => k === idx ? { ...e, ...patch } : e) });
  const renameExtra = (idx, name) => {
    const old = extras[idx].target;
    u({
      extra_outputs: extras.map((e, k) => k === idx ? { ...e, target: name } : e),
      cases: cases.map(cs => {
        if (!cs.sets || !(old in cs.sets)) return cs;
        const { [old]: moved, ...rest } = cs.sets;
        return { ...cs, sets: { ...rest, [name]: moved } };
      }),
    });
  };
  const removeExtra = (idx) => {
    const old = extras[idx].target;
    u({
      extra_outputs: extras.filter((_, k) => k !== idx),
      cases: cases.map(cs => {
        if (!cs.sets || !(old in cs.sets)) return cs;
        const { [old]: _gone, ...rest } = cs.sets;
        return { ...cs, sets: rest };
      }),
    });
  };
  const setCaseSet = (i, name, r) => updateCase(i, { sets: { ...(cases[i].sets || {}), [name]: { then_mode: r.mode, then: r.value, then_calc: r.calc } } });
  const sourceCtx = { secondaryDatasets, sheets, pipeline, currentStep: step, runMap, onBrowse: onBrowseJoinFile ? () => onBrowseJoinFile(step.id) : undefined, buildTargetName: pipeline?.inputFile?.buildTargetName };
  const outNames = outputNamesOf(c);
  const outCheck = validateOutputNames(outNames);
  const addCondition = (i) => updateCase(i, { when: [...(cases[i].when || []), { column: (cols[0]?.name || ''), op: 'eq', value: '' }] });
  const removeCondition = (i, j) => updateCase(i, { when: cases[i].when.filter((_, k) => k !== j) });
  return (
    <div>
      <div style={{ marginBottom: 10 }}>
        <Segmented
          value={codeView ? 'code' : 'builder'}
          onChange={chooseBlockMode}
          options={[{ value: 'builder', label: 'Builder' }, { value: 'code', label: 'Code' }]}
        />
      </div>
      <div style={{ marginBottom: 10 }}>
        <FieldLabel hint="Dataset to evaluate cases over — an uploaded sheet, an earlier published step, or this block's own data">Source</FieldLabel>
        <SourceSelect
          value={c.source}
          onChange={(v) => u({ source: v })}
          secondaryDatasets={secondaryDatasets}
          sheets={sheets}
          pipeline={pipeline}
          currentStep={step}
          onBrowse={onBrowseJoinFile ? () => onBrowseJoinFile(step.id) : undefined}
          buildTargetName={pipeline?.inputFile?.buildTargetName}
        />
      </div>

      {codeView && (
        <>
          <OutputColumnField
            value={c.target}
            onChange={(v) => u({ target: v })}
            placeholder="e.g. tier"
            hint="The code's returned value (or its object key of this name) is written here"
          />
          <div style={{ marginTop: 6 }}>
            <WriteModeChoice value={c.write_mode} onChange={(v) => u({ write_mode: v })} />
            {outCheck.errors[0] && <InlineError>{outCheck.errors[0]}</InlineError>}
          </div>
          {extras.map((ex, idx) => (
            <div key={idx} style={{ background: C.input, border: `1px solid ${C.border}`, borderRadius: 8, padding: 10, marginTop: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Eyebrow style={{ marginBottom: 0 }}>Output column</Eyebrow>
                <TextInput mono value={ex.target} onChange={(v) => renameExtra(idx, v)} placeholder="column name" />
                <IconButton title="Remove output column" onClick={() => removeExtra(idx)}><X size={14} /></IconButton>
              </div>
              <div style={{ marginTop: 8 }}>
                <WriteModeChoice value={ex.write_mode} onChange={(v) => patchExtra(idx, { write_mode: v })} />
              </div>
              {outCheck.errors[idx + 1] && <InlineError>{outCheck.errors[idx + 1]}</InlineError>}
            </div>
          ))}
          <div style={{ marginTop: 10 }}>
            <Button kind="ghost" size="sm" onClick={addExtra} icon="＋" title="Let the code also set another column">Add output column</Button>
          </div>
          {outputNamesOf(c).length > 1 && (
            <div style={{ color: C.fg5, fontSize: '.74rem', marginTop: 8 }}>
              With more than one output the code must return an object keyed by these column names.
            </div>
          )}

          <div style={{ background: C.input, border: `1px solid ${C.border}`, borderRadius: 8, padding: 10, marginTop: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <Eyebrow style={{ marginBottom: 0 }}>Code</Eyebrow>
              <div style={{ flex: 1 }} />
              <Button kind="accent" size="sm" onClick={() => setEditing(true)}>{hasSavedBlockCode(c) ? 'Edit code' : 'Write code'}</Button>
            </div>
            {needsBlockReview(c) && (
              <div style={{ background: 'var(--warn-card-bg)', border: '1px solid var(--warn-border)', color: C.warn, borderRadius: 8, padding: '8px 10px', fontSize: '.78rem', marginBottom: 8 }}>
                Imported code: open the editor and Apply to enable it
              </div>
            )}
            {hasSavedBlockCode(c) ? (() => {
              const fl = firstLines(c.code.src, 8);
              return (
                <pre title="Edit code to change it" style={{
                  margin: 0, fontFamily: "'JetBrains Mono', monospace", fontSize: '.76rem', color: C.fg2,
                  background: C.app, border: `1px solid ${C.border}`, borderRadius: 6, padding: '6px 8px',
                  overflowX: 'auto', whiteSpace: 'pre',
                }}>{fl.text}{fl.more ? `\n… ${fl.more} more line${fl.more === 1 ? '' : 's'}` : ''}</pre>
              );
            })() : (
              <div style={{ color: C.fg5, fontSize: '.78rem' }}>No code yet. Write a function body that returns the value for each row.</div>
            )}
            <div style={{ color: C.fg5, fontSize: '.72rem', marginTop: 8 }}>
              The dropdown cases, else and extra values are kept but not used while this block is in Code mode.
            </div>
          </div>
        </>
      )}

      {!codeView && (<>
      <OutputColumnField
        value={c.target}
        onChange={(v) => u({ target: v })}
        placeholder="e.g. tier"
        hint="Each case writes its 'then' value into this column"
      />

      {cases.map((cs, i) => (
        <div key={i} style={{
          background: C.input, border: `1px solid ${C.border}`,
          borderRadius: 8, padding: 10, marginTop: 10,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <span style={{
              background: 'var(--info-bg)', color: C.accentBright,
              border: `1px solid ${C.accentMuted}`,
              fontSize: '.7rem', fontWeight: 700, letterSpacing: '.06em',
              padding: '2px 8px', borderRadius: 6, textTransform: 'uppercase',
            }}>When {i + 1}</span>
            <div style={{ flex: 1 }} />
            <IconButton title="Remove case" onClick={() => removeCase(i)}><X size={14} /></IconButton>
          </div>
          {isCodeCase(cs) ? (
            <div style={{ marginBottom: 4 }}>
              <div style={{ color: C.fg4, fontSize: '.76rem', marginBottom: 6 }}>
                Legacy per-case code (read-only): switch the block to Code mode.
              </div>
              {needsReview(cs) && (
                <div style={{ background: 'var(--warn-card-bg)', border: '1px solid var(--warn-border)', color: C.warn, borderRadius: 8, padding: '8px 10px', fontSize: '.78rem', marginBottom: 6 }}>
                  Imported expression: not enabled until it is reviewed. Per-case code can no longer be edited here.
                </div>
              )}
              <div title={cs.expr?.src || ''} style={{
                fontFamily: "'JetBrains Mono', monospace", fontSize: '.78rem', color: C.fg2,
                background: C.app, border: `1px solid ${C.border}`, borderRadius: 6,
                padding: '6px 8px', marginBottom: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>{oneLine(cs.expr?.src || '')}</div>
            </div>
          ) : (
            <>
              {(cs.when || []).length > 1 && (
                <div style={{ marginBottom: 8 }}>
                  <Eyebrow style={{ marginBottom: 6 }}>Match</Eyebrow>
                  <Segmented
                    value={cs.conjunction || 'AND'}
                    onChange={(v) => updateCase(i, { conjunction: v })}
                    options={[{ value: 'AND', label: 'ALL · AND' }, { value: 'OR', label: 'ANY · OR' }]}
                  />
                </div>
              )}
              <ConditionRows
                conditions={cs.when || []}
                columns={cols}
                onUpdate={(j, patch) => updateCase(i, { when: cs.when.map((x, k) => k === j ? { ...x, ...patch } : x) })}
                onRemove={(cs.when || []).length > 1 ? (j) => removeCondition(i, j) : undefined}
                variant="plain"
                sourceCtx={sourceCtx}
              />
              <Button kind="ghost" size="sm" onClick={() => addCondition(i)} icon="＋">Add condition</Button>
            </>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
            <span style={{ color: C.fg4, fontSize: '.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', minWidth: 30 }}>Then</span>
            <ResultValueField
              mode={cs.then_mode || 'literal'} value={cs.then} calc={cs.then_calc} columns={cols}
              onChange={(r) => updateCase(i, { then_mode: r.mode, then: r.value, then_calc: r.calc })}
            />
          </div>
          {extras.map((ex) => (
            <div key={ex.target} style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
              <span title={ex.target} style={{ color: C.fg4, fontSize: '.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', width: 60, flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Then · {ex.target}</span>
              <ResultValueField
                mode={cs.sets?.[ex.target]?.then_mode || 'literal'} value={cs.sets?.[ex.target]?.then} calc={cs.sets?.[ex.target]?.then_calc} columns={cols}
                onChange={(r) => setCaseSet(i, ex.target, r)}
              />
            </div>
          ))}
        </div>
      ))}
      <Button kind="ghost" size="sm" onClick={addCase} icon="＋" style={{ marginTop: 10 }}>Add case</Button>

      <FieldLabel hint="If no case matches">Else</FieldLabel>
      <div style={{ display: 'flex' }}>
        <ResultValueField
          mode={c.else_mode || 'literal'} value={c.else} calc={c.else_calc} columns={cols} allowKeep placeholder="default value"
          onChange={(r) => u({ else_mode: r.mode, else: r.value, else_calc: r.calc })}
        />
      </div>

      <label style={{
        display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer',
        color: C.fg4, fontSize: '.78rem', marginTop: 12,
      }} title="Leave rows alone when the target column already holds a value">
        <input type="checkbox" checked={c.write_mode === 'only_if_blank'}
          onChange={(e) => u({ write_mode: e.target.checked ? 'only_if_blank' : 'always' })}
          style={{ accentColor: C.accent }} />
        Only write where the target column is blank
      </label>

      {extras.map((ex, idx) => (
        <div key={idx} style={{ background: C.input, border: `1px solid ${C.border}`, borderRadius: 8, padding: 10, marginTop: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <Eyebrow style={{ marginBottom: 0 }}>Output column</Eyebrow>
            <TextInput mono value={ex.target} onChange={(v) => renameExtra(idx, v)} placeholder="column name" />
            <IconButton title="Remove output column" onClick={() => removeExtra(idx)}><X size={14} /></IconButton>
          </div>
          <FieldLabel hint="If no case matches — each case's value for this column is set inside the case above">Else</FieldLabel>
          <div style={{ display: 'flex' }}>
            <ResultValueField
              mode={ex.else_mode || 'literal'} value={ex.else} calc={ex.else_calc} columns={cols} allowKeep placeholder="default value"
              onChange={(r) => patchExtra(idx, { else_mode: r.mode, else: r.value, else_calc: r.calc })}
            />
          </div>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', color: C.fg4, fontSize: '.78rem', marginTop: 10 }}>
            <input type="checkbox" checked={ex.write_mode === 'only_if_blank'}
              onChange={(e) => patchExtra(idx, { write_mode: e.target.checked ? 'only_if_blank' : 'always' })}
              style={{ accentColor: C.accent }} />
            Only write where this column is blank
          </label>
        </div>
      ))}
      <div style={{ marginTop: 10 }}>
        <Button kind="ghost" size="sm" onClick={addExtra} icon="＋" title="Let each case also set another column">Add output column</Button>
      </div>

      </>)}

      <OutputNameField
        value={c.output_name}
        onChange={(v) => u({ output_name: v })}
        required={outputNameMissing}
        hint={sourceIsSelf
          ? 'Optional — also publish this result under a name so later blocks can pick it as their Source'
          : "Required — Source isn't self, so this step's result doesn't touch the live pipeline payload; without a name it isn't reachable anywhere"}
        warningMessage={<>Source is &ldquo;{c.source}&rdquo;, not self — without an Output Name this step&rsquo;s result goes nowhere: the live pipeline payload stays unchanged and the rows aren&rsquo;t saved anywhere.</>}
        placeholder="e.g., mas_tiered"
        issue={outputNameIssueRes}
      />

      {editing && (
        <ExpressionEditorModal
          kind="value_body"
          title="Code editor"
          caseLabel={outNames.filter(n => n.trim()).join(', ')}
          initialSrc={c.code?.src || ''}
          columns={cols}
          rows={editorRows.rows}
          rowsOrigin={editorRows.origin}
          rowsNote={editorRows.note}
          outputs={outNames}
          convertConfig={blockConvertSource(c)}
          showConvertHint={showFirstOpenHint(c, hasSavedBlockCode(c))}
          outputError={outCheck.first}
          pipelineId={pipeline?.id}
          onClose={() => { setEditing(false); if (!hasSavedBlockCode(c)) setCodeDraft(false); }}
          onApply={(src) => {
            setCodeDraft(false);
            setEditing(false);
            u(codeBlockPatch(src));
          }}
        />
      )}
    </div>
  );
}

function WriteModeChoice({ value, onChange }) {
  return (
    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', color: C.fg4, fontSize: '.78rem' }}>
      <input type="checkbox" checked={value === 'only_if_blank'}
        onChange={(e) => onChange(e.target.checked ? 'only_if_blank' : 'always')}
        style={{ accentColor: C.accent }} />
      Only write where this column is blank
    </label>
  );
}

function InlineError({ children }) {
  return <div style={{ color: C.error, fontSize: '.76rem', marginTop: 4 }}>{children}</div>;
}
